import {
  Badge,
  ExternalIcon,
  titleCase,
  DescItem,
  formatDate,
  Skeleton,
} from '@setel/portal-ui';
import { AxiosError } from 'axios';
import { environments } from 'environments/environment';
import {
  CustomDescList,
  ViewOptionProps,
} from 'modules/accounts/account-details';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { pageview } from 'shared/analytics/gtag';
import { loyaltyCardStatusColor } from 'shared/consts/loyalty.const';
import { useIndexUserLoyaltyCards } from '../loyalty-queries';

export function LoyaltyInfo({ userId, isUserProfileLoaded }: ViewOptionProps) {
  // added ga for url tracking
  let location = useLocation();
  React.useEffect(() => {
    pageview(location.pathname, 'Loyalty');
  }, [location]);

  const { data: loyaltyCard, isError } = useIndexUserLoyaltyCards(userId, {
    enabled: isUserProfileLoaded,
    retry: (retryCount, err) => {
      const error = err as AxiosError;
      return (
        error.response?.status !== 404 &&
        error.response?.data?.message !== 'Cannot get card from api-loyalty' &&
        retryCount < 3
      );
    },
  });

  return (
    <>
      <div className="border-gray-200 border">
        {isError ? (
          <div className="break-words text-lg m-3">Loyalty card not found</div>
        ) : (
          <React.Fragment>
            <CustomDescList>
              <DescItem
                label="Mesra card"
                value={
                  loyaltyCard ? (
                    loyaltyCard.cardNumber
                  ) : (
                    <Skeleton className="animate-pulse" width="medium" />
                  )
                }
              />
              <DescItem
                label="Card type"
                value={
                  loyaltyCard ? (
                    loyaltyCard?.isPhysicalCard ? (
                      'Physical'
                    ) : (
                      'Virtual'
                    )
                  ) : (
                    <Skeleton className="animate-pulse" width="medium" />
                  )
                }
                valueClassName="mt-2"
              />
              <DescItem
                valueClassName="mt-2"
                label="Status"
                value={
                  loyaltyCard ? (
                    <Badge
                      color={
                        loyaltyCardStatusColor[loyaltyCard.status] || 'grey'
                      }
                      className="uppercase"
                    >
                      {titleCase(loyaltyCard?.status || '-', {
                        hasUnderscore: true,
                      }) || '-'}
                    </Badge>
                  ) : (
                    <Skeleton className="animate-pulse" width="medium" />
                  )
                }
              />
              <DescItem
                valueClassName="mt-2"
                label="Balance"
                value={
                  loyaltyCard ? (
                    loyaltyCard?.pointTotalBalance ? (
                      loyaltyCard?.pointTotalBalance + ' pts'
                    ) : (
                      '0 pts'
                    )
                  ) : (
                    <Skeleton className="animate-pulse" width="medium" />
                  )
                }
              />
              <DescItem
                valueClassName="mt-2"
                label="Created on"
                value={
                  loyaltyCard ? (
                    formatDate(loyaltyCard.createdAt)
                  ) : (
                    <Skeleton className="animate-pulse" width="medium" />
                  )
                }
              />
            </CustomDescList>
            <hr className="mx-4" />
            <a
              rel="noreferrer"
              href={
                loyaltyCard?.provider === 'SETEL'
                  ? `${environments.webAdminUrl}/loyalty/members/${loyaltyCard?.id}`
                  : `${environments.webAdminUrl}/accounts/loyalty-cards/${loyaltyCard?.id}`
              }
              className="text-brand-500 tracking-1 font-semibold my-4 text-center flex justify-center"
              target="_blank"
            >
              <span className="text-xs">VIEW MORE </span>
              <ExternalIcon className="ml-2 inline" />
            </a>
          </React.Fragment>
        )}
      </div>
    </>
  );
}
